import { Button, Collapse, Flex, List, Modal, Tag, Typography } from "antd";

const toArray = (value) => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const SKIPPED_REASON_LABELS = {
  not_from_table: "задача не связана со строкой таблицы",
  multiple_links: "найдено несколько связей со строками таблицы",
  not_participant: "вы не участник рабочей группы задачи",
  final_status: "задача завершена, отменена или удалена",
  unchanged: "описание и срок уже совпадают",
};

const FAILED_REASON_LABELS = {
  server_error: "ошибка сервера",
};

const getTaskUuid = (task) => task?.task_uuid || task?.uuid;

const getTaskLabel = (task, requestedTasksByUuid) => {
  const taskUuid = getTaskUuid(task);
  const taskNumber =
    task?.task_number ?? requestedTasksByUuid[taskUuid]?.task_number;

  return taskNumber ? `Задача №${taskNumber}` : `Задача ${taskUuid || ""}`.trim();
};

const TaskList = ({ items, requestedTasksByUuid, reasonLabels }) => (
  <List
    bordered
    size="small"
    dataSource={items}
    style={{ maxHeight: 400, overflowY: "auto", borderRadius: 0 }}
    renderItem={(item, index) => {
      const reason = reasonLabels?.[item?.reason] || item?.reason;

      return (
        <List.Item key={getTaskUuid(item) || index}>
          <Flex vertical gap={2}>
            <Typography.Text strong>
              {getTaskLabel(item, requestedTasksByUuid)}
            </Typography.Text>
            {reason && (
              <Typography.Text type="secondary">{reason}</Typography.Text>
            )}
          </Flex>
        </List.Item>
      );
    }}
  />
);

const TableTasksSyncResultModal = ({
  visible,
  result,
  requestedTasks = [],
  onCancel,
}) => {
  const updatedCount = Number(result?.updated) || 0;
  const skippedItems = toArray(result?.skipped);
  const failedItems = toArray(result?.failed);
  const requestedTasksByUuid = Object.fromEntries(
    requestedTasks.map((task) => [task.uuid, task]),
  );
  const notUpdatedUuids = new Set(
    [...skippedItems, ...failedItems].map(getTaskUuid),
  );
  const updatedItems = requestedTasks.filter(
    (task) => !notUpdatedUuids.has(task.uuid),
  );
  const resultSections = [
    updatedItems.length && {
      key: "updated",
      label: (
        <Flex align="center" gap={8}>
          <span>Изменены</span>
          <Tag color="green" variant="filled">
            {updatedItems.length}
          </Tag>
        </Flex>
      ),
      children: <TaskList items={updatedItems} requestedTasksByUuid={requestedTasksByUuid} />,
    },
    skippedItems.length && {
      key: "skipped",
      label: (
        <Flex align="center" gap={8}>
          <span>Не изменены</span>
          <Tag color="gold" variant="filled">
            {skippedItems.length}
          </Tag>
        </Flex>
      ),
      children: (
        <TaskList
          items={skippedItems}
          requestedTasksByUuid={requestedTasksByUuid}
          reasonLabels={SKIPPED_REASON_LABELS}
        />
      ),
    },
    failedItems.length && {
      key: "failed",
      label: (
        <Flex align="center" gap={8}>
          <span>Не удалось обновить</span>
          <Tag color="red" variant="filled">
            {failedItems.length}
          </Tag>
        </Flex>
      ),
      children: (
        <TaskList
          items={failedItems}
          requestedTasksByUuid={requestedTasksByUuid}
          reasonLabels={FAILED_REASON_LABELS}
        />
      ),
    },
  ].filter(Boolean);
  const defaultOpenSections =
    requestedTasks.length <= 10
      ? resultSections.map((section) => section.key)
      : resultSections
          .filter((section) => section.key === "failed")
          .map((section) => section.key);

  return (
    <Modal
      open={visible}
      title="Обновление задач завершено"
      width={640}
      destroyOnHidden
      onCancel={onCancel}
      footer={<Button onClick={onCancel}>Понятно</Button>}
    >
      <Flex vertical gap={16}>
        <Flex wrap gap={8}>
          <Tag color="green">Изменены: {updatedCount}</Tag>
          <Tag color="gold">Не изменены: {skippedItems.length}</Tag>
          <Tag color="red">Ошибки: {failedItems.length}</Tag>
        </Flex>
        <Collapse
          size="small"
          items={resultSections}
          defaultActiveKey={defaultOpenSections}
          style={{ borderRadius: 0 }}
        />
      </Flex>
    </Modal>
  );
};

export default TableTasksSyncResultModal;
